/**
 * Platform-aware install hints for the system tools Foundry's spawned
 * subprocesses depend on. Used by:
 *
 * - `OnboardingOverlay` — surfaces missing prereqs at first launch so
 *   users know what they need before they paste an Anthropic key.
 * - `Dashboard` pre-Create gate — blocks game creation when a prereq
 *   is missing instead of letting the user wait for `pnpm install` to
 *   ENOENT mid-flow.
 * - `Settings → Diagnostics → System tools` — passive listing of
 *   ✓/✗ + version + install link for triage.
 *
 * The "platform" parameter is the renderer's `navigator.platform`
 * (we don't have access to `process.platform` from the renderer's
 * context-isolated bridge). Substring match is enough — exact values
 * vary across Electron versions ("MacIntel" vs "Mac68K" vs "iPhone").
 *
 * Install commands are the lowest-friction path that lands the right
 * thing on PATH:
 *   - macOS: Homebrew if available; we don't try to detect it, just
 *     surface the brew command alongside the .pkg fallback.
 *   - Windows: winget for everything we can; node + pnpm via the
 *     Node.js installer if winget isn't available.
 *   - Linux/other: a generic web link rather than guessing distro.
 */
export const PREREQ_INSTALL_HINTS = {
    git: {
        tool: 'git',
        label: 'git',
        why: "Foundry uses git to clone the hello-weekend template, commit your scaffold, and push to your game's GitHub repo on publish.",
        commands: [
            { platform: 'mac', command: 'brew install git' },
            { platform: 'windows', command: 'winget install --id Git.Git -e' },
            { platform: 'other', command: '' },
        ],
        docsUrl: 'https://git-scm.com/downloads',
    },
    node: {
        tool: 'node',
        label: 'Node.js (≥ 22)',
        why: "pnpm itself runs on Node, so the system Node.js has to be on PATH even though Foundry's GUI bundles its own runtime.",
        commands: [
            { platform: 'mac', command: 'brew install node@22' },
            { platform: 'windows', command: 'winget install --id OpenJS.NodeJS.LTS -e' },
            { platform: 'other', command: '' },
        ],
        docsUrl: 'https://nodejs.org/',
    },
    pnpm: {
        // Issue #310: this row's `available` flag now reflects the
        // **Foundry pnpm launch cascade**: Corepack first, then the
        // bundled fallback, then ambient pnpm. Corepack remains the
        // preferred route because it resolves the game's `packageManager`
        // pin, but a stripped Node distribution without Corepack should
        // not block game creation when Foundry's fallback can run pnpm.
        tool: 'pnpm',
        label: 'Node.js + Corepack',
        why: 'Foundry prefers Corepack (normally bundled with Node 22+) so pnpm follows the game `package.json` pin, then falls back to the bundled pnpm when Corepack is missing.',
        commands: [
            // The Node row already covers installing Node itself. When this
            // row is still missing on a machine that has Node, Homebrew-style
            // stripped distributions can usually be repaired by installing
            // Corepack through npm (see issue #930).
            { platform: 'mac', command: 'npm install -g corepack' },
            { platform: 'windows', command: 'npm install -g corepack' },
            { platform: 'other', command: 'npm install -g corepack' },
        ],
        docsUrl: 'https://nodejs.org/api/corepack.html',
    },
};
/**
 * Best command for the running platform — substring match against
 * `navigator.platform` (renderer-side). Falls back to the `'other'`
 * entry when the platform is neither Mac nor Windows.
 */
export function commandFor(hint, platform) {
    const lower = platform.toLowerCase();
    const wanted = lower.includes('mac')
        ? 'mac'
        : lower.includes('win')
            ? 'windows'
            : 'other';
    const match = hint.commands.find((c) => c.platform === wanted);
    return match?.command ?? '';
}
//# sourceMappingURL=system-prereqs.js.map